import { getTodayInZambia, getZambiaDayBounds } from "@/lib/zambia-time";

export type ActivityRange = "today" | "week" | "month" | "all";

export type SaleActivityRow = {
  id: string;
  item_name: string | null;
  quantity: number | null;
  final_amount: number | null;
  total_price: number | null;
  created_at: string | null;
  sold_by?: string | null;
  voided_at?: string | null;
};

export type ExpenseActivityRow = {
  id: string;
  category: string | null;
  description: string | null;
  amount: number;
  expense_date: string;
  date: string | null;
  created_by?: string | null;
};

export type ActivityEntry = {
  id: string;
  type: "sale" | "expense";
  occurred_at: string;
  actor_id: string | null;
  actor_label?: string;
  title: string;
  quantity?: number | null;
  amount: number;
  canDelete?: boolean;
};

function dateStringToUtcMidday(dateStr: string) {
  return new Date(`${dateStr}T12:00:00.000Z`);
}

function formatDateString(date: Date) {
  return date.toISOString().slice(0, 10);
}

function getWeekStart(todayStr: string) {
  const date = dateStringToUtcMidday(todayStr);
  const day = date.getUTCDay();
  const offset = day === 0 ? -6 : 1 - day;
  date.setUTCDate(date.getUTCDate() + offset);
  return formatDateString(date);
}

function getMonthStart(todayStr: string) {
  return `${todayStr.slice(0, 8)}01`;
}

const ZAMBIA_OFFSET_MS = 2 * 60 * 60 * 1000;

function toZambiaDate(value: string | Date) {
  const date = typeof value === "string" ? new Date(value) : value;
  return new Date(date.getTime() + ZAMBIA_OFFSET_MS);
}

export function getRangeQueryBounds(range: ActivityRange) {
  if (range === "all") {
    return null;
  }
  const today = getTodayInZambia();
  const startDate = range === "today" ? today : range === "week" ? getWeekStart(today) : getMonthStart(today);
  const endDate = today;
  const { start } = getZambiaDayBounds(startDate);
  const { end } = getZambiaDayBounds(endDate);
  return {
    startDate,
    endDate,
    startIso: start.toISOString(),
    endIso: end.toISOString(),
  };
}

export function buildActivityEntries(
  sales: SaleActivityRow[],
  expenses: ExpenseActivityRow[],
  actorLabelById: Record<string, string> = {},
  options?: {
    allowDeleteSales?: boolean;
    saleOwnerId?: string | null;
  },
) {
  const entries: ActivityEntry[] = [];

  sales.forEach((sale) => {
    if (!sale.created_at || sale.voided_at) return;
    const amount = Number(sale.final_amount ?? sale.total_price ?? 0);
    const quantity = sale.quantity ?? null;
    const actorId = sale.sold_by ?? null;
    entries.push({
      id: sale.id,
      type: "sale",
      occurred_at: sale.created_at,
      actor_id: actorId,
      actor_label: actorId ? actorLabelById[actorId] ?? undefined : undefined,
      title: sale.item_name || "Sale",
      quantity,
      amount,
      canDelete:
        Boolean(options?.allowDeleteSales) &&
        Boolean(options?.saleOwnerId) &&
        actorId === options?.saleOwnerId &&
        isWithinLast24Hours(sale.created_at),
    });
  });

  expenses.forEach((expense) => {
    const occurredAt = expense.date ?? `${expense.expense_date}T12:00:00.000Z`;
    const actorId = expense.created_by ?? null;
    entries.push({
      id: expense.id,
      type: "expense",
      occurred_at: occurredAt,
      actor_id: actorId,
      actor_label: actorId ? actorLabelById[actorId] ?? undefined : undefined,
      title: expense.description?.trim() || expense.category || "Expense",
      amount: Number(expense.amount || 0),
    });
  });

  return entries.sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime());
}

export function groupActivityEntries(entries: ActivityEntry[]) {
  const groups = new Map<string, ActivityEntry[]>();
  entries.forEach((entry) => {
    const key = formatActivityGroupLabel(entry.occurred_at);
    groups.set(key, [...(groups.get(key) ?? []), entry]);
  });
  return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
}

export function summarizeActivity(entries: ActivityEntry[]) {
  const sales = entries.filter((entry) => entry.type === "sale");
  const expenses = entries.filter((entry) => entry.type === "expense");
  return {
    salesCount: sales.length,
    salesTotal: sales.reduce((sum, entry) => sum + Number(entry.amount || 0), 0),
    expenseCount: expenses.length,
  };
}

export function formatActivityTime(iso: string) {
  const date = toZambiaDate(iso);
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

export function formatActivityGroupLabel(iso: string) {
  const date = toZambiaDate(iso);
  const weekdays = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const months = [
    "JANUARY",
    "FEBRUARY",
    "MARCH",
    "APRIL",
    "MAY",
    "JUNE",
    "JULY",
    "AUGUST",
    "SEPTEMBER",
    "OCTOBER",
    "NOVEMBER",
    "DECEMBER",
  ];
  return `${weekdays[date.getUTCDay()]} ${date.getUTCDate()} ${months[date.getUTCMonth()]}`;
}

export function formatKwacha(amount: number) {
  return `K ${amount.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export function isWithinLast24Hours(iso: string) {
  const diffMs = Date.now() - new Date(iso).getTime();
  return diffMs >= 0 && diffMs <= 24 * 60 * 60 * 1000;
}
